import { useAppSelector, useAppDispatch } from 'hooks/reduxHooks'
import { fetchProductSubscription } from 'actions/LuxPlusActions'
import LuxPlusLandingPageContent from 'luxPlus/components/LuxPlusLandingPageContent'
import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import LuxPlusMonthLandingPageContent from 'takeovers/luxPlusMonthTakeover/components/LuxPlusMonthLandingPageContent'
import { LUX_PLUS_MONTH_TAKEOVER_ID } from 'takeovers/luxPlusMonthTakeover/constants/luxPlusMonthTakeoverConstants'
import useCurrentSiteTakeover from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import { selectLoggedIn } from 'selectors/accountSelectors'
import AccountAccessModal, { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal'
import ModalContext from 'contexts/ModalContext'
import purchaseLuxPlus from 'actions/luxPlus/purchaseLuxPlus'
import useQueryParams from 'hooks/useQueryParams'
import { LUX_PLUS_PROMOTIONAL_OFFER_EXPIRY_DATE, LUX_PLUS_PROMOTIONAL_OFFER_PARAM } from 'luxPlus/constants/base'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { dateIsBefore } from 'lib/datetime/dateUtils'

function LuxPlusLandingPage() {
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchProductSubscription())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const currentTakeover = useCurrentSiteTakeover()

  const isLoggedIn = useAppSelector(selectLoggedIn)
  const showModal = useContext(ModalContext)
  const openLoginModal = useCallback(() => showModal<AccountAccessModalResult>(<AccountAccessModal />), [showModal])

  const regionCode = useAppSelector(state => state.geo.currentRegionCode)
  const isLuxPlusMember = useAppSelector(getCanRedeemLuxPlusBenefits)
  const queryParams = useQueryParams()
  const hasPromotionParam = queryParams.get(LUX_PLUS_PROMOTIONAL_OFFER_PARAM) === 'true'
  const dateIsBeforePromotionExpiry = dateIsBefore(new Date(), new Date(LUX_PLUS_PROMOTIONAL_OFFER_EXPIRY_DATE))

  const isLuxPlusPromotionalOfferEnabled = useMemo(() => {
    return (
      regionCode === 'AU' &&
      !isLuxPlusMember &&
      dateIsBeforePromotionExpiry &&
      hasPromotionParam
    )
  }, [regionCode, isLuxPlusMember, dateIsBeforePromotionExpiry, hasPromotionParam])

  const purchasePromotionalLuxPlusOffer = useCallback(async() => {
    if (!isLuxPlusPromotionalOfferEnabled) return
    if (!isLoggedIn) {
      const loginResult = await openLoginModal()
      if (loginResult.loggedIn) {
        dispatch(purchaseLuxPlus({ hasJoinFee: false }))
      }
    } else {
      dispatch(purchaseLuxPlus({ hasJoinFee: false }))
    }
  }, [isLuxPlusPromotionalOfferEnabled, isLoggedIn, openLoginModal, dispatch])

  const subscriptionProduct = useAppSelector(state => state.luxPlus.products.subscription)

  useEffect(() => {
    purchasePromotionalLuxPlusOffer()
  }, [purchasePromotionalLuxPlusOffer, subscriptionProduct.offers])

  if (!isSpoofed && currentTakeover?.id === LUX_PLUS_MONTH_TAKEOVER_ID) return <LuxPlusMonthLandingPageContent />

  return <LuxPlusLandingPageContent />
}

export default LuxPlusLandingPage
